import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api-client.js';
import {
  WEEKDAY_LABELS_SHORT,
  addMonths,
  addDays,
  endOfMonth,
  eventsOnDay,
  formatLongDay,
  formatMonthYear,
  formatTime,
  formatWeekRange,
  isSameDay,
  isSameMonth,
  monthGrid,
  startOfDay,
  startOfMonth,
  weekGrid,
  type CalendarEvent,
} from '../lib/calendar-utils.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody } from '../components/ui/card.js';
import { TimeGridView } from '../components/calendar-time-grid.js';

type View = 'month' | 'week' | 'day';

interface Event extends CalendarEvent {
  attendee_name?: string;
  attendee_phone: string;
}

interface ListResponse {
  data: Event[];
  pagination: { next_cursor: string | null; has_more: boolean };
}

const STATUS_TONE: Record<string, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  pending: 'neutral',
  awaiting: 'warning',
  confirmed: 'success',
  failed: 'danger',
};

const DRAG_MIME = 'application/x-xsc-event';

interface DragPayload {
  id: string;
  start_at: string;
}

interface DropTargetProps {
  dragOverISO: string | null;
  setDragOverISO: (iso: string | null) => void;
  onDropEvent: (eventId: string, sourceISO: string, targetDay: Date) => void;
}

export function CalendarPage(): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [focus, setFocus] = useState<Date>(() => startOfDay(new Date()));
  const [view, setView] = useState<View>('month');
  const [dragOverISO, setDragOverISO] = useState<string | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);

  // Pull a window slightly larger than the visible month so multi-day events
  // crossing boundaries still render. The month grid shows 6 weeks = 42 days.
  const windowStart = useMemo(() => addDays(startOfMonth(focus), -7), [focus]);
  const windowEnd = useMemo(() => addDays(endOfMonth(focus), 7), [focus]);

  const { data, isLoading, error } = useQuery<ListResponse>({
    queryKey: ['events', windowStart.toISOString(), windowEnd.toISOString()],
    queryFn: () =>
      api.get(
        `/v1/events?from=${encodeURIComponent(windowStart.toISOString())}&to=${encodeURIComponent(windowEnd.toISOString())}&limit=100`,
      ) as Promise<ListResponse>,
  });

  const events: Event[] = data?.data ?? [];

  // Reschedule mutation. Used by every drag/resize gesture (date-only, time
  // precision, edge-resize). Optimistically updates every cached events
  // query; rolls back on error and shows a banner. onSettled invalidates so
  // the cache re-converges with the server.
  const reschedule = useMutation({
    mutationFn: ({ id, start_at, end_at }: { id: string; start_at: string; end_at: string }) =>
      api.patch(`/v1/events/${id}`, { start_at, end_at }),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ['events'] });
      const snapshots = qc.getQueriesData<ListResponse>({ queryKey: ['events'] });
      for (const [key, snap] of snapshots) {
        if (!snap) continue;
        qc.setQueryData<ListResponse>(key, {
          ...snap,
          data: snap.data.map((e) =>
            e.id === vars.id ? { ...e, start_at: vars.start_at, end_at: vars.end_at } : e,
          ),
        });
      }
      return { snapshots };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.snapshots) {
        for (const [key, snap] of ctx.snapshots) {
          qc.setQueryData(key, snap);
        }
      }
      const detail =
        err instanceof ApiError && typeof err.body === 'object' && err.body && 'detail' in err.body
          ? String((err.body as { detail: unknown }).detail)
          : (err as Error).message;
      setBannerError(`Failed to reschedule — change reverted. ${detail}`);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['events'] });
    },
  });

  // Month-view drop: day-granularity. Shift start + end by the day delta.
  const onDropEvent = (eventId: string, sourceISO: string, targetDay: Date): void => {
    const ev = events.find((e) => e.id === eventId);
    if (!ev) return;
    const sourceMidnight = startOfDay(new Date(sourceISO));
    const targetMidnight = startOfDay(targetDay);
    const dayDelta = Math.round((targetMidnight.getTime() - sourceMidnight.getTime()) / 86_400_000);
    if (dayDelta === 0) return;
    const newStart = addDays(new Date(ev.start_at), dayDelta).toISOString();
    const newEnd = addDays(new Date(ev.end_at), dayDelta).toISOString();
    setBannerError(null);
    reschedule.mutate({ id: eventId, start_at: newStart, end_at: newEnd });
  };

  // Time-grid drop: precise time. New start_at = drop position; preserve
  // duration. The drag payload includes the original start_at but we don't
  // need it here — we look the event up by id and read its current state.
  const onDropToTime = (eventId: string, _sourceISO: string, targetTime: Date): void => {
    const ev = events.find((e) => e.id === eventId);
    if (!ev) return;
    if (targetTime.getTime() === new Date(ev.start_at).getTime()) return;
    const duration = new Date(ev.end_at).getTime() - new Date(ev.start_at).getTime();
    const newEnd = new Date(targetTime.getTime() + duration);
    setBannerError(null);
    reschedule.mutate({
      id: eventId,
      start_at: targetTime.toISOString(),
      end_at: newEnd.toISOString(),
    });
  };

  // Click empty grid → quick-create. Routes to /events/new with both
  // start_at and end_at prefilled. Time-grid clicks pass start + 30min;
  // all-day-row clicks pass midnight + 24h. The new-event form reads
  // both from URL search params.
  const onCreateAt = (start: Date, end: Date): void => {
    const params = new URLSearchParams({
      start: start.toISOString(),
      end: end.toISOString(),
    });
    navigate(`/events/new?${params.toString()}`);
  };

  // Bottom-edge resize: keep start_at, change end_at.
  const onResize = (eventId: string, newEnd: Date): void => {
    const ev = events.find((e) => e.id === eventId);
    if (!ev) return;
    if (newEnd.getTime() === new Date(ev.end_at).getTime()) return;
    setBannerError(null);
    reschedule.mutate({
      id: eventId,
      start_at: ev.start_at,
      end_at: newEnd.toISOString(),
    });
  };

  // Top-edge resize: change start_at, keep end_at.
  const onResizeStart = (eventId: string, newStart: Date): void => {
    const ev = events.find((e) => e.id === eventId);
    if (!ev) return;
    if (newStart.getTime() === new Date(ev.start_at).getTime()) return;
    setBannerError(null);
    reschedule.mutate({
      id: eventId,
      start_at: newStart.toISOString(),
      end_at: ev.end_at,
    });
  };

  const goPrev = (): void => setFocus((d) => {
    if (view === 'month') return addMonths(d, -1);
    if (view === 'week') return addDays(d, -7);
    return addDays(d, -1);
  });
  const goNext = (): void => setFocus((d) => {
    if (view === 'month') return addMonths(d, 1);
    if (view === 'week') return addDays(d, 7);
    return addDays(d, 1);
  });
  const goToday = (): void => setFocus(startOfDay(new Date()));

  const headerLabel =
    view === 'month' ? formatMonthYear(focus)
    : view === 'week' ? formatWeekRange(focus)
    : formatLongDay(focus);

  const monthDropProps: DropTargetProps = { dragOverISO, setDragOverISO, onDropEvent };
  const weekDays = useMemo(() => weekGrid(focus), [focus]);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold">{headerLabel}</h1>
          <div className="flex gap-1">
            <Button variant="secondary" size="sm" onClick={goPrev} aria-label="Previous">
              ‹
            </Button>
            <Button variant="secondary" size="sm" onClick={goToday}>
              Today
            </Button>
            <Button variant="secondary" size="sm" onClick={goNext} aria-label="Next">
              ›
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ViewToggle value={view} onChange={setView} />
          <Link to="/events/new">
            <Button>New event</Button>
          </Link>
        </div>
      </header>

      {error && <ErrorBanner error={error} />}
      {bannerError && (
        <div className="flex items-start justify-between gap-2 rounded border border-danger bg-surface p-3 text-sm">
          <span className="text-danger">{bannerError}</span>
          <button
            type="button"
            onClick={() => setBannerError(null)}
            className="text-ink-muted hover:text-ink"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
      {isLoading && !data && <p className="text-sm text-ink-muted">Loading…</p>}

      {data && view === 'month' && (
        <MonthView
          focus={focus}
          events={events}
          onSelectDay={(d) => { setFocus(d); setView('day'); }}
          {...monthDropProps}
        />
      )}
      {data && view === 'week' && (
        <TimeGridView
          days={weekDays}
          events={events}
          onDropToTime={onDropToTime}
          onResize={onResize}
          onResizeStart={onResizeStart}
          onCreateAt={onCreateAt}
        />
      )}
      {data && view === 'day' && (
        <TimeGridView
          days={[focus]}
          events={events}
          onDropToTime={onDropToTime}
          onResize={onResize}
          onResizeStart={onResizeStart}
          onCreateAt={onCreateAt}
        />
      )}
    </div>
  );
}

// ============================================================================
// Month grid (day-granularity drag-drop reschedule)
// ============================================================================

interface MonthViewProps extends DropTargetProps {
  focus: Date;
  events: Event[];
  onSelectDay: (d: Date) => void;
}

function MonthView({
  focus,
  events,
  onSelectDay,
  dragOverISO,
  setDragOverISO,
  onDropEvent,
}: MonthViewProps): JSX.Element {
  const grid = useMemo(() => monthGrid(focus), [focus]);
  const today = useMemo(() => startOfDay(new Date()), []);

  return (
    <Card>
      <CardBody className="p-0">
        <div className="grid grid-cols-7 border-b border-border bg-surface-alt text-ink-muted text-xs font-medium">
          {WEEKDAY_LABELS_SHORT.map((label) => (
            <div key={label} className="px-2 py-2 text-center">
              {label}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 grid-rows-6 divide-x divide-y divide-border">
          {grid.flat().map((day) => {
            const inMonth = isSameMonth(day, focus);
            const isToday = isSameDay(day, today);
            const dayEvents = eventsOnDay(events, day);
            const visible = dayEvents.slice(0, 3);
            const overflow = dayEvents.length - visible.length;
            const dayISO = day.toISOString();
            const isDropTarget = dragOverISO === dayISO;
            // Day cell is a div-with-button-semantics so we can nest event
            // chips as <a> tags inside (nesting <a> in <button> is invalid).
            return (
              <div
                key={dayISO}
                role="button"
                tabIndex={0}
                onClick={() => onSelectDay(day)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectDay(day);
                  }
                }}
                onDragOver={(e) => onCellDragOver(e, dayISO, dragOverISO, setDragOverISO)}
                onDragLeave={(e) => onCellDragLeave(e, dayISO, dragOverISO, setDragOverISO)}
                onDrop={(e) => onCellDrop(e, day, setDragOverISO, onDropEvent)}
                className={`min-h-24 px-1 py-1 text-left flex flex-col gap-1 transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-accent focus:z-10 ${
                  inMonth ? 'bg-surface' : 'bg-surface-alt text-ink-muted'
                } ${isDropTarget ? 'ring-2 ring-accent ring-inset bg-accent/10' : 'hover:bg-surface-alt'}`}
              >
                <div
                  className={`text-xs font-medium self-end ${
                    isToday
                      ? 'inline-flex items-center justify-center w-5 h-5 rounded-full bg-accent text-accent-fg'
                      : ''
                  }`}
                >
                  {day.getDate()}
                </div>
                <div className="space-y-0.5">
                  {visible.map((e) => (
                    <EventChip key={e.id} event={e} />
                  ))}
                  {overflow > 0 && (
                    <div className="text-[10px] text-ink-muted px-1">+{overflow} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardBody>
    </Card>
  );
}

function EventChip({ event }: { event: Event }): JSX.Element {
  const tone = STATUS_TONE[event.status] ?? 'neutral';
  const TONE: Record<string, string> = {
    neutral: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200',
    info: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200',
    success: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200',
    warning: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200',
    danger: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200',
  };
  return (
    <Link
      to={`/events/${event.id}`}
      onClick={(e) => e.stopPropagation()}
      draggable
      onDragStart={(e) => {
        e.stopPropagation();
        e.dataTransfer.effectAllowed = 'move';
        const payload: DragPayload = { id: event.id, start_at: event.start_at };
        e.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
        e.dataTransfer.setData('text/plain', event.title);
      }}
      title={`${formatTime(new Date(event.start_at))} — ${event.title} (drag to reschedule)`}
      className={`block truncate rounded px-1 py-0.5 text-[11px] font-medium hover:ring-1 hover:ring-current cursor-grab active:cursor-grabbing ${TONE[tone]}`}
    >
      <span className="opacity-75 mr-1">{formatTime(new Date(event.start_at))}</span>
      {event.title}
    </Link>
  );
}

// ============================================================================
// Drag-and-drop cell handlers (Month view only — Week + Day are time-grid)
// ============================================================================

function onCellDragOver(
  e: React.DragEvent,
  dayISO: string,
  dragOverISO: string | null,
  setDragOverISO: (iso: string | null) => void,
): void {
  // Only treat this as a valid drop target if our MIME type is on the
  // dataTransfer. types[] is available here even though getData() is not.
  if (!e.dataTransfer.types.includes(DRAG_MIME)) return;
  e.preventDefault();
  e.dataTransfer.dropEffect = 'move';
  if (dragOverISO !== dayISO) setDragOverISO(dayISO);
}

function onCellDragLeave(
  e: React.DragEvent,
  dayISO: string,
  dragOverISO: string | null,
  setDragOverISO: (iso: string | null) => void,
): void {
  // Don't clear if we're moving into a child element (the chip inside the cell).
  if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
  if (dragOverISO === dayISO) setDragOverISO(null);
}

function onCellDrop(
  e: React.DragEvent,
  day: Date,
  setDragOverISO: (iso: string | null) => void,
  onDropEvent: (eventId: string, sourceISO: string, targetDay: Date) => void,
): void {
  e.preventDefault();
  setDragOverISO(null);
  const raw = e.dataTransfer.getData(DRAG_MIME);
  if (!raw) return;
  try {
    const payload = JSON.parse(raw) as DragPayload;
    onDropEvent(payload.id, payload.start_at, day);
  } catch {
    // dataTransfer didn't have our payload — silently ignore non-event drops.
  }
}

// ============================================================================
// View toggle + error banner
// ============================================================================

function ViewToggle({ value, onChange }: { value: View; onChange: (v: View) => void }): JSX.Element {
  return (
    <div className="inline-flex rounded border border-border overflow-hidden text-sm">
      {(['month', 'week', 'day'] as const).map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={`px-3 py-1 ${
            value === v
              ? 'bg-accent text-accent-fg'
              : 'bg-surface text-ink-muted hover:text-ink hover:bg-surface-alt'
          }`}
        >
          {v[0]!.toUpperCase() + v.slice(1)}
        </button>
      ))}
    </div>
  );
}

function ErrorBanner({ error }: { error: unknown }): JSX.Element {
  const status = error instanceof ApiError ? error.status : 0;
  return (
    <div className="rounded border border-danger bg-surface p-3 text-sm">
      <p className="font-medium text-danger">Failed to load events</p>
      <p className="mt-1 text-ink-muted">
        {status ? `HTTP ${status}` : 'Network error'} — {(error as Error).message}
      </p>
    </div>
  );
}
